import { supabase } from "@/lib/supabase";
import {
  STATS,
  INTEL_COUNTERS,
  TESTIMONIALS,
  CASE_STUDIES,
  INSIGHTS,
  MARKETS,
  compensationOptions,
  calculateCompensation,
} from "@/lib/staticData";

// All read-only content is served locally from staticData.js — no server needed.
// All write operations (leads, CV submissions) go to Supabase.

const LEAD_FIELDS = ["slug", "title", "market", "service", "role", "institution", "summary", "image", "metrics"];
const INSIGHT_FIELDS = ["slug", "title", "category", "date", "reading_time", "author", "image", "excerpt"];
const MARKET_FIELDS = ["slug", "name", "tagline", "currency", "image", "intro", "stats"];

const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));

export const api = {
  getStats: () => Promise.resolve({ stats: STATS, counters: INTEL_COUNTERS }),

  getTestimonials: () => Promise.resolve(TESTIMONIALS),

  getCaseStudies: () => Promise.resolve(CASE_STUDIES.map((cs) => pick(cs, LEAD_FIELDS))),

  getCaseStudy: (slug) => {
    const cs = CASE_STUDIES.find((c) => c.slug === slug);
    if (!cs) return Promise.reject(new Error("Case study not found"));
    return Promise.resolve(cs);
  },

  getInsights: () => Promise.resolve(INSIGHTS.map((a) => pick(a, INSIGHT_FIELDS))),

  getInsight: (slug) => {
    const insight = INSIGHTS.find((a) => a.slug === slug);
    if (!insight) return Promise.reject(new Error("Insight not found"));
    return Promise.resolve(insight);
  },

  getMarkets: () => Promise.resolve(MARKETS.map((m) => pick(m, MARKET_FIELDS))),

  getMarket: (slug) => {
    const market = MARKETS.find((m) => m.slug === slug);
    if (!market) return Promise.reject(new Error("Market not found"));
    return Promise.resolve(market);
  },

  getCompOptions: () => Promise.resolve(compensationOptions()),

  calcComp: ({ role, level, market }) => {
    const result = calculateCompensation(role, level, market);
    if (!result) return Promise.reject(new Error("Invalid role, level or market."));
    return Promise.resolve(result);
  },

  createLead: async (payload) => {
    const { error } = await supabase.from("leads").insert([{
      type: payload.type || "contact",
      name: payload.name || null,
      email: payload.email || null,
      role: payload.role || null,
      institution: payload.institution || null,
      market: payload.market || null,
      service: payload.service || null,
      message: payload.message || null,
      consent: payload.consent || false,
      meta: payload.meta || null,
    }]);
    if (error) throw new Error(error.message);
    return { success: true, message: "Thank you. Your request has been received." };
  },

  createCvLead: async (formData) => {
    const email = formData.get("email");
    const name = formData.get("name");
    const market = formData.get("market");
    const consent = formData.get("consent") === "true";
    const file = formData.get("file");

    let fileMeta = null;
    let fileUrl = null;

    if (file && file.size > 0) {
      const ext = file.name.split(".").pop();
      const path = `cvs/${Date.now()}_${email.replace(/[^a-z0-9]/gi, "_")}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("cv-uploads")
        .upload(path, file, { contentType: file.type, upsert: false });

      if (uploadError) {
        console.warn("CV file upload failed:", uploadError.message);
      } else {
        const { data: urlData } = supabase.storage.from("cv-uploads").getPublicUrl(path);
        fileUrl = urlData?.publicUrl || null;
        fileMeta = { filename: file.name, content_type: file.type, size: file.size, url: fileUrl };
      }
    }

    const { error } = await supabase.from("leads").insert([{
      type: "cv",
      email,
      name: name || null,
      market: market || null,
      consent,
      meta: { file: fileMeta },
    }]);
    if (error) throw new Error(error.message);
    return { success: true, message: "Your CV has been received in confidence. We will be in touch." };
  },
};

export default api;
